import { useMemo, useState } from "react";
import { useApi } from "../../api/hooks";
import { EtatChargement, EtatErreur } from "../../composants/Avis";
import { Tuiles } from "../../composants/Tableau";
import { ListeDocumentsSchema } from "../../api/schemas";
import type { EcranProps } from "../types";

/**
 * §5.D — Checklist d'audit documentaire, PARTAGÉE PAR TOUTE L'ÉQUIPE.
 *
 * La clé de groupe est FIXE, pour que les coches restent stables quand la
 * configuration évolue — même règle que la checklist des obligations (§5.I).
 */
const CHECKLIST = [
  {
    groupe: "juridique",
    titre: "1. Juridique",
    points: [
      { cle: "jur-statuts", libelle: "Statuts à jour", indice: ["statut"] },
      { cle: "jur-rccm", libelle: "Extrait RCCM", indice: ["rccm"] },
      { cle: "jur-dfe", libelle: "Déclaration fiscale d'existence", indice: ["dfe"] },
      { cle: "jur-bail", libelle: "Bail ou titre d'occupation", indice: ["bail"] },
      { cle: "jur-pv", libelle: "Procès-verbaux d'assemblée", indice: ["assemblee", "proces"] },
    ],
  },
  {
    groupe: "fiscal",
    titre: "2. Fiscal",
    points: [
      { cle: "fis-decl", libelle: "Déclarations mensuelles déposées", indice: ["declaration"] },
      { cle: "fis-quit", libelle: "Quittances de paiement", indice: ["quittance"] },
      { cle: "fis-pat", libelle: "Patente de l'exercice", indice: ["patente"] },
      { cle: "fis-301", libelle: "États 301", indice: ["301"] },
    ],
  },
  {
    groupe: "social",
    titre: "3. Social",
    points: [
      { cle: "soc-contrats", libelle: "Contrats de travail écrits", indice: ["contrat"] },
      { cle: "soc-bulletins", libelle: "Bulletins de paie", indice: ["bulletin", "paie"] },
      { cle: "soc-cnps", libelle: "Attestations CNPS", indice: ["cnps"] },
      { cle: "soc-visites", libelle: "Visites médicales", indice: ["medical", "visite"] },
    ],
  },
  {
    groupe: "comptable",
    titre: "4. Comptable",
    points: [
      { cle: "cpt-releves", libelle: "Relevés bancaires de tous les comptes", indice: ["releve", "bancaire"] },
      { cle: "cpt-factures", libelle: "Factures de vente", indice: ["facture"] },
      { cle: "cpt-achats", libelle: "Factures d'achat", indice: ["achat", "fournisseur"] },
      { cle: "cpt-caisse", libelle: "Pièces de caisse", indice: ["caisse"] },
      { cle: "cpt-immo", libelle: "Justificatifs d'immobilisations", indice: ["immobilisation", "materiel"] },
    ],
  },
] as const;

const CLE_STOCKAGE = (dossierId: string) => `mdc.audit.${dossierId}`;

export function AuditDocumentaire({ dossierId, annee }: EcranProps) {
  const q = useApi(
    ["documents", dossierId],
    `/dossiers/${dossierId}/documents`,
    ListeDocumentsSchema,
  );

  // La checklist est partagée par l'équipe : elle vit côté serveur en
  // production. Ici l'état local sert de brouillon avant enregistrement.
  const [coches, setCoches] = useState<Record<string, boolean>>(() => {
    const brut = localStorage.getItem(CLE_STOCKAGE(dossierId));
    return brut ? (JSON.parse(brut) as Record<string, boolean>) : {};
  });

  function basculer(cle: string) {
    const suivant = { ...coches, [cle]: !coches[cle] };
    setCoches(suivant);
    localStorage.setItem(CLE_STOCKAGE(dossierId), JSON.stringify(suivant));
  }

  const docs = q.data ?? [];

  /** Un point est « constaté » si une pièce du dossier en porte l'indice. */
  const constate = useMemo(() => {
    const index = new Map<string, boolean>();
    for (const g of CHECKLIST)
      for (const p of g.points)
        index.set(
          p.cle,
          docs.some((d) => {
            const chemin = d.cheminLogique.toLowerCase();
            return p.indice.some((i) => chemin.includes(i));
          }),
        );
    return index;
  }, [docs]);

  if (q.isLoading) return <EtatChargement quoi="de l'audit documentaire" />;
  if (q.error) return <EtatErreur erreur={q.error} />;

  const total = CHECKLIST.reduce((s, g) => s + g.points.length, 0);
  const constates = [...constate.values()].filter(Boolean).length;
  const validesManuellement = Object.values(coches).filter(Boolean).length;

  return (
    <div>
      <p className="resume">
        Checklist des pièces attendues, <strong>partagée par toute l'équipe</strong>.
        Les clés de groupe sont fixes : les coches restent stables quand la
        configuration évolue.
      </p>

      <Tuiles
        tuiles={[
          { titre: "Points de contrôle", valeur: total },
          { titre: "Constatés au dossier", valeur: constates },
          { titre: "Validés manuellement", valeur: validesManuellement },
          {
            titre: "Manquants",
            valeur: total - constates,
            alerte: total - constates > 0,
          },
        ]}
      />

      {CHECKLIST.map((g) => (
        <section key={g.groupe}>
          <h2>{g.titre}</h2>
          <ul className="checklist">
            {g.points.map((p) => {
              const auto = constate.get(p.cle) ?? false;
              return (
                <li key={p.cle} className={auto ? "constate" : "manquant"}>
                  <label>
                    <input
                      type="checkbox"
                      checked={coches[p.cle] ?? false}
                      onChange={() => basculer(p.cle)}
                    />
                    {p.libelle}
                  </label>
                  <span className={auto ? "oui" : "non"}>
                    {auto ? "pièce présente au dossier" : "aucune pièce détectée"}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      ))}

      <p className="note">
        Exercice {annee}. Le constat automatique repose sur l'emplacement des
        pièces ; la coche manuelle reste la décision du collaborateur.
      </p>
    </div>
  );
}
